/* eslint-disable @next/next/no-img-element */
import {
  AuthFill,
  Button,
  Email,
  FirstName,
  LastName,
  MarketingConsent,
  ModelGrid,
  Title,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import {
  closeOverlay,
  getModelImage,
  getModels,
  getPageData,
} from '@utils/helpers'
import { formEvent } from '@utils/helpers/tracking'
import { BrochureSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from 'upper-case-first'

const Brochure = ({
  models,
  content,
  tracking,
  settings,
  brand,
  dealer,
  model,
  query,
}) => {
  const campaign = useCampaign()
  const [formStep, setFormStep] = useState(1)
  const [user, setUser] = useState(false)
  const [isVehicleExpanded, expandVehicle] = useState(false)
  const [isDetailsExpanded, expandDetails] = useState(false)
  const [formData, setFormData] = useState({})
  const [submitResponse, setSubmitResponse] = useState('')

  const step = (newFormStep, userState) => {
    setUser(userState)
    setFormStep(newFormStep)
  }

  const initialValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    model: '',
    consent: {
      user: false,
    },
    campaign_response_code: '',
    campaign_code: '',
    response_code: '',
    dealer: '',
    dealername: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(BrochureSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const VehicleStep = () =>
    formData.modelName ? (
      <div
        className={`relative ${brand === 'lexus' ? ' uppercase' : ''}`}
        data-gt-name="componentevent"
        data-gt-componentname="brochure-request"
      >
        <div className="md:flex items-center">
          <button
            className="flex hover:underline"
            onClick={() => {
              step(1, true)
              expandDetails(false)
              expandVehicle(false)
              setFormData((state) => {
                return {
                  ...state,
                  modelImage: '',
                  modelName: '',
                  fuel: null,
                }
              })
              setSubmitResponse('')
            }}
            // data-gt-componentgroup="forms"
            // data-gt-componentname="brochure-request"
            // data-gt-name="navevent"
            // data-gt-action="nav-click-cta"
            // data-gt-label="change vehicle"
          >
            <div className="flex flex-col items-baseline md:flex-row md:space-x-2 md:justify-between">
              <div
                className={`font-medium ${
                  brand === 'lexus' ? ' uppercase' : ''
                }`}
              >
                1. {brand === 'lexus' ? 'Car' : 'Vehicle'}
              </div>
              <span className="text-tiny">Change</span>
            </div>
          </button>
        </div>
        <button
          className="md:flex text-tiny hidden mt-1"
          data-gt-action={`nav-${
            isVehicleExpanded ? 'open' : 'close'
          }-dropdown`}
          data-gt-label="vehicle"
          onClick={() => {
            expandVehicle((state) => !state)
            expandDetails(false)
          }}
        >
          <div>{formData.modelName}</div>
          {formData.label && <div className="ml-1"> {formData.label}</div>}

          <div className="ml-1 mt-1">
            {isVehicleExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            )}
          </div>
        </button>
        {isVehicleExpanded && (
          <div
            className="absolute top-[70px] p-[15px] shadow-md z-[10] w-[300px] bg-white"
            data-gt-componentgroup="forms"
            data-gt-name="componentevent"
            data-gt-componentname="brochure-request"
          >
            <div className="flex justify-between">
              <div>
                <div className="text-lg font-medium">{formData.modelName}</div>
                {formData.fuel !== undefined &&
                  formData.fuel.toLowerCase() !== 'petrol' &&
                  formData.fuel.toLowerCase() !== 'diesel' && (
                    <div
                      className={`${
                        brand === 'toyota' ? 'font-medium text-blue' : ''
                      } uppercase`}
                    >
                      {formData.fuel}
                    </div>
                  )}
              </div>

              <button
                onClick={() => {
                  expandVehicle((state) => !state)
                  expandDetails(false)
                }}
                data-gt-action="nav-close-dropdown"
                data-gt-label="vehicle"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <img
              className="w-[100%] font-medium mt-4"
              src={formData.modelImage}
              alt={formData.modelName}
            />
          </div>
        )}
      </div>
    ) : (
      <div className="font-semibold">
        1. {brand === 'lexus' ? 'Car' : 'Vehicle'}
      </div>
    )

  const DetailsStep = () =>
    formData.first_name ? (
      <div className={`relative ${brand === 'lexus' ? ' uppercase' : ''}`}>
        <div className="md:flex items-center">
          <button
            className="flex md:ml-[10px] hover:underline"
            onClick={() => {
              step(4, true)
              expandDealer(false)
              expandDetails(false)
              expandVehicle(false)
              setFormData((state) => {
                return {
                  ...state,
                  first_name: '',
                  last_name: '',
                  email: '',
                  phone: '',
                }
              })
            }}
            // data-gt-componentgroup="forms"
            // data-gt-componentname="brochure-request"
            // data-gt-name="navevent"
            // data-gt-action="nav-click-cta"
            // data-gt-label="change details"
          >
            <div className="flex flex-col items-baseline md:flex-row md:space-x-2 md:justify-between">
              <div
                className={`font-medium ${
                  brand === 'lexus' ? ' uppercase' : ''
                }`}
              >
                2. Details
              </div>
              <span className="text-tiny">Change</span>
            </div>
          </button>
        </div>
        <button
          className="md:flex text-[13px] mt-[10px] leading-[10px] invisible lg:visible"
          onClick={() => {
            expandDetails((state) => !state)
            expandDealer(false)
            expandVehicle(false)
          }}
          data-gt-action={`${isDetailsExpanded ? 'open' : 'close'}-dropdown`}
          data-gt-label="details"
        >
          <div>{`${formData.first_name} `} </div>

          <div className="ml-1 mt-1">
            {isDetailsExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 15.75l7.5-7.5 7.5 7.5"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            )}
          </div>
        </button>
        {isDetailsExpanded && (
          <div
            className="absolute top-[70px] p-[15px] shadow-md z-[10] w-[300px] bg-white"
            data-gt-componentgroup="forms"
            data-gt-name="componentevent"
            data-gt-componentname="brochure-request"
          >
            <div className="flex justify-between  ">
              <div className="">
                {`${formData.first_name} ${formData.last_name}`}
              </div>

              <button
                onClick={() => {
                  expandDetails((state) => !state)
                  expandDealer(false)
                  expandVehicle(false)
                }}
                data-gt-action="nav-close-dropdown"
                data-gt-label="details"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className=""> {`${formData.email} `}</div>
          </div>
        )}
      </div>
    ) : (
      <div className="flex flex-row">
        <div className={`${formStep === 2 ? 'font-semibold' : 'font-medium'}`}>
          2. Details
        </div>
      </div>
    )

  useEffect(() => {
    if (dealer) {
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )
      methods.setValue('dealername', dealer?.name ? dealer.name : '')
    }
  }, [dealer])

  const onSubmit = async (data) => {
    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/brochure`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent({
          conversion: 1,
          name: tracking.form,
          step: 'form-submit',
          variant: '',
          page: tracking.page,
        })
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent({
          conversion: 0,
          name: tracking.form,
          step: 'form-error',
          variant: '',
          page: tracking.page,
        })
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.brochure) {
      methods.setValue('campaign_response_code', campaign.brochure)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (model) {
      methods.setValue('model', model.d4t4.id)
      setFormData((state) => {
        return {
          ...state,
          modelImage: getModelImage(
            brand,
            model?.image?.id,
            model?.image?.grade,
            model?.image?.color,
            'webp',
            model?.image?.fallback,
          ),
          modelName: model.name,
          fuel: model?.filters?.fuel[0],
        }
      })
      if (query?.method === 'email') {
        step(2, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
        steps={[VehicleStep(), DetailsStep()]}
      />
      <FormWrapper>
        <FormStep
          title={content.car.title}
          step={formStep}
          active={formStep === 1}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'select-model',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <ModelGrid
            type="brochure"
            models={models}
            pricelist={settings?.pricelist_url}
            showFilter={content.car.filter}
            setModel={(selectedModel) => {
              setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
              }, 100)
              methods.setValue('model', selectedModel.d4t4.id)
              methods.setValue(
                'response_code',
                dealer
                  ? selectedModel?.responsecodes?.dealer_email || ''
                  : selectedModel?.responsecodes?.national_email || '',
              )
              setFormData((state) => {
                return {
                  ...state,
                  modelImage: getModelImage(
                    brand,
                    selectedModel?.image?.id,
                    selectedModel?.image?.grade,
                    selectedModel?.image?.color,
                    'webp',
                    selectedModel?.image?.fallback,
                  ),
                  modelName: selectedModel.name,
                  fuel: selectedModel?.filters?.fuel[0],
                }
              })
              step(2, true)
            }}
          />
        </FormStep>
        <FormStep
          title={content.details.title}
          step={formStep}
          active={formStep === 2}
          user={user}
          tracking={{
            conversion: 0,
            name: tracking.form,
            step: 'contact-details',
            variant: tracking.variant,
            page: tracking.page,
          }}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Title />
              <FirstName />
              <LastName />
              <Email />
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'brochure'
  const content = await import(`../content/${brand}/forms/${form}.md`).catch(
    () => null,
  )
  const settings = await import(`../content/${brand}/settings/global.md`).catch(
    () => null,
  )

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      models: getModels(brand, form),
      content: content.attributes,
      settings: settings.attributes,
      title: content.attributes.title,
      tracking: {
        page: 'brochure-request',
        form: 'lead-brochure-request',
        variant: 'default',
        initialStep: 'select-model',
      },
      description: `${upperCaseFirst(
        brand,
      )} Brochures contain everything you need to know about our range of vehicles. Find out more information on our website or get in touch with us directly.`,
      ...(await getPageData(context, form)),
    },
  }
}

Brochure.whyDidYouRender = true

export default Brochure
